import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Database, Table2, Activity, Menu, X, LogIn, LogOut, Sparkles, User as UserIcon, Map, Bell, BookOpen, FileUp, Webhook, Flame } from "lucide-react";
import { HexLogo } from "./Hex";
import { NAV, ALERTS } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";

const items = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: NAV.dashboardLink, end: true },
  { to: "/hot-deals", label: "Hot Deals", icon: Flame, testid: "nav-hot-deals-link" },
  { to: "/sources", label: "Sources", icon: Database, testid: NAV.sourcesLink },
  { to: "/properties", label: "Properties", icon: Table2, testid: NAV.propertiesLink },
  { to: "/imports", label: "My Imports", icon: FileUp, testid: "nav-imports-link" },
  { to: "/coverage", label: "Coverage", icon: Map, testid: "nav-coverage-link" },
  { to: "/alerts", label: "Alerts", icon: Bell, testid: ALERTS.navLink },
  { to: "/runs", label: "Runs", icon: Activity, testid: NAV.runsLink },
  { to: "/getting-started", label: "Getting Started", icon: BookOpen, testid: "nav-getting-started-link" },
];

const adminItem = { to: "/admin/webhooks", label: "Webhook Health", icon: Webhook, testid: "nav-webhooks-link" };

function useNavItems() {
  const { user } = useAuth();
  return user?.is_admin ? [...items, adminItem] : items;
}

const linkBase =
  "flex items-center gap-3 px-4 py-3 border-l-4 font-display text-sm font-semibold tracking-tight transition-colors";
const linkInactive =
  "border-transparent text-zinc-600 hover:text-black hover:bg-yellow-50";
const linkActive = "border-yellow-500 bg-yellow-50 text-black";

function AccountBlock({ compact = false, onNavigate = () => {} }) {
  const { user, isSubscribed, subscription, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className={compact ? "px-4 pb-4" : "px-6 py-4 border-t-2 border-black"}>
        <button
          data-testid="nav-signin-btn"
          onClick={() => { onNavigate(); navigate("/login"); }}
          className="w-full flex items-center justify-center gap-2 border-2 border-black bg-yellow-400 py-2 font-display font-semibold text-sm shadow-brutal-sm hover:bg-yellow-300"
        >
          <LogIn size={14} strokeWidth={2.5} /> Sign in with Google
        </button>
      </div>
    );
  }

  return (
    <div className={compact ? "px-4 pb-4 space-y-3" : "px-6 py-4 border-t-2 border-black space-y-3"}>
      <div className="flex items-center gap-2">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 border-2 border-black" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-8 h-8 border-2 border-black flex items-center justify-center bg-zinc-100">
            <UserIcon size={14} />
          </div>
        )}
        <div className="min-w-0">
          <div className="font-display font-semibold text-sm truncate">{user.name || user.email}</div>
          <div className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 truncate">
            {isSubscribed ? `${subscription?.plan || "active"} · ${subscription?.status}` : "free preview"}
          </div>
        </div>
      </div>
      {!isSubscribed ? (
        <button
          data-testid="nav-upgrade-btn"
          onClick={() => { onNavigate(); navigate("/pricing"); }}
          className="w-full flex items-center justify-center gap-2 border-2 border-black bg-red-600 text-white py-2 font-display font-semibold text-sm shadow-brutal-sm hover:bg-red-500"
        >
          <Sparkles size={14} strokeWidth={2.5} /> Start Free Trial
        </button>
      ) : null}
      <button
        data-testid="nav-logout-btn"
        onClick={async () => { await logout(); onNavigate(); navigate("/login"); }}
        className="w-full flex items-center justify-center gap-2 border-2 border-zinc-300 py-1.5 font-mono-data text-[11px] uppercase tracking-widest text-zinc-600 hover:border-black hover:text-black"
      >
        <LogOut size={12} /> Sign out
      </button>
    </div>
  );
}

export function Sidebar() {
  const navItems = useNavItems();
  return (
    <aside className="hidden md:flex md:flex-col md:w-64 lg:w-72 shrink-0 bg-white border-r-2 border-black">
      <div
        data-testid={NAV.brand}
        className="flex items-center gap-3 px-6 py-6 border-b-2 border-black bg-brand-black text-white"
      >
        <HexLogo size={48} />
        <div>
          <div className="font-brand text-xl leading-none text-red-500">
            LIEN SCOUT
          </div>
          <div className="font-mono-data text-[9px] uppercase tracking-[0.18em] text-yellow-400 mt-1.5">
            AI Powered Property Intelligence
          </div>
        </div>
      </div>
      <nav className="flex-1 py-4">
        {navItems.map(({ to, label, icon: Icon, testid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
              `${linkBase} ${isActive ? linkActive : linkInactive}`
            }
          >
            <Icon size={18} strokeWidth={2.25} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <AccountBlock />
    </aside>
  );
}

export function MobileHeader() {
  const [open, setOpen] = useState(false);
  const navItems = useNavItems();
  return (
    <div className="md:hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-brand-black border-b-2 border-black">
        <div data-testid={NAV.brand} className="flex items-center gap-2">
          <HexLogo size={34} />
          <div className="font-brand text-lg text-red-500">LIEN SCOUT</div>
        </div>
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="p-2 border-2 border-yellow-400 bg-black text-yellow-400 shadow-brutal-sm"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>
      {open && (
        <nav className="bg-white border-b-2 border-black">
          {navItems.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : linkInactive}`
              }
            >
              <Icon size={18} strokeWidth={2.25} />
              <span>{label}</span>
            </NavLink>
          ))}
          <div className="border-t-2 border-black pt-3">
            <AccountBlock compact onNavigate={() => setOpen(false)} />
          </div>
        </nav>
      )}
    </div>
  );
}
